/**
 * InformationBlockEditor - visual-first authoring card for an information block.
 *
 * An information block shows the respondent an image (a screenshot, chart, or
 * mockup) and gives holito a directive to react to it during the live voice
 * call. The image bytes never reach the voice worker: an AI caption generated
 * at authoring time is holito's only surrogate for the image, so the caption is
 * load-bearing realtime data and publish is gated on it.
 *
 * Layout (two columns):
 * - LEFT: a fixed 192x128 image slot. Icon-only dashed dropzone at rest (click
 *   OR drag-and-drop a file onto it), a themed Skeleton while uploading, a
 *   thumbnail on success (clickable to open a lightbox), and a "couldn't load
 *   preview" fallback if the served image fails. The thumbnail prefers an
 *   instant local object-URL preview so it shows immediately and does not depend
 *   on the public serve route resolving in the authoring environment.
 * - RIGHT: two bare auto-growing textareas (title, directive). While the AI is
 *   generating them, themed Skeletons stand in (the same loading primitive the
 *   rest of the form uses for AI-generated elements).
 *
 * The caption (holito's surrogate) is NOT shown here: it is editable in the
 * card's advanced settings. The editor still requests and persists it.
 *
 * Anti-overwrite race rule: the title/directive the model returns are applied
 * only when the author has not typed into that field AND the live value still
 * equals the snapshot taken when the caption request was issued. The caption
 * itself always applies.
 *
 * @module components/form-builder/InformationBlockEditor
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FormField, InformationBlockFieldConfig } from '../../types';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { offlineSessionWorkerUrl } from '../../lib/worker-urls';
import { isRetryableHttpStatus } from '../../lib/generation-errors';
import { Skeleton } from '../ui/Skeleton';
import { KlipyPicker } from './KlipyPicker';
import { KlipyAttribution } from '../klipy/KlipyAttribution';
import type { KlipyGif } from '../../lib/klipy';
import {
  INFORMATION_BLOCK_ALLOWED_MIME_TYPES,
  INFORMATION_BLOCK_MAX_BYTES,
  INFORMATION_BLOCK_MIME_REJECT_MESSAGE,
  informationBlockSizeRejectMessage,
  isAllowedInformationBlockMime,
} from '../../lib/information-blocks/constants';

/** A sibling field's shape projected for caption context. */
export interface SurroundingFieldSummary {
  /** The sibling field's admin-facing label. */
  label: string;
  /** The sibling field's type discriminator. */
  type: FormField['type'];
}

/** Patch shape the editor emits: only information-block config keys. */
type InformationBlockPatch = Partial<
  Pick<
    InformationBlockFieldConfig,
    | 'imageStorageKey'
    | 'body'
    | 'contentVariant'
    | 'imageSource'
    | 'imageUrl'
    | 'imageStillUrl'
    | 'mimeType'
    | 'gifId'
    | 'gifSlug'
    | 'title'
    | 'directive'
    | 'caption'
  >
>;

/** Props accepted by `InformationBlockEditor`. */
export interface InformationBlockEditorProps {
  /** The information-block field being edited. */
  field: FormField & InformationBlockFieldConfig;
  /** Owning workspace id: used in the upload/caption/delete worker calls. */
  workspaceId?: string;
  /** Merge a partial patch into this field. */
  onUpdateField: (patch: InformationBlockPatch) => void;
  /** Other fields' labels+types, for caption grounding. */
  surroundingFields?: SurroundingFieldSummary[];
  /** Optional campaign goal, for caption grounding. */
  campaignGoal?: string;
  /**
   * Storage keys of info-block images already referenced by the SAVED
   * campaign (edit mode). Removing or replacing an image whose key is in
   * this set must NOT delete the R2 object: the saved config (and any
   * in-flight session) still renders it, and cancelling the edit must leave
   * it intact. Absent in the create flow, so behaviour there is unchanged.
   */
  committedImageKeys?: ReadonlySet<string>;
}

/** MIME types offered in the file picker (SVG is selectable then rejected). */
const FILE_INPUT_ACCEPT = [...INFORMATION_BLOCK_ALLOWED_MIME_TYPES, 'image/svg+xml'].join(',');

/** Caption pipeline lifecycle for the right-column suggestions. */
type CaptionStatus = 'idle' | 'pending' | 'failed';

/** Shape of the upload worker response. */
interface UploadResponse {
  imageStorageKey: string;
  imageUrl: string;
  mimeType: string;
}

/** Shape of the caption worker response. */
interface CaptionResponse {
  title?: string;
  directive?: string;
  caption?: string;
}

/**
 * Create an object URL for an instant local preview, tolerating environments
 * (jsdom / SSR) where `URL.createObjectURL` is unavailable.
 */
function safeCreateObjectUrl(file: File): string | null {
  try {
    if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
      return URL.createObjectURL(file);
    }
  } catch {
    /* unsupported environment: fall back to the served URL */
  }
  return null;
}

/** Revoke an object URL, ignoring environments that lack the API. */
function safeRevokeObjectUrl(url: string | null): void {
  if (!url) return;
  try {
    if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
      URL.revokeObjectURL(url);
    }
  } catch {
    /* noop */
  }
}

/** Plus glyph for the empty dropzone. */
const PlusGlyph: React.FC = () => (
  <svg
    aria-hidden="true"
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
  </svg>
);

/** Eye glyph for the lightbox veil. */
const EyeGlyph: React.FC = () => (
  <svg
    aria-hidden="true"
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z"
    />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

/** Small auto-growing textarea with no border or fill. */
const BareTextarea: React.FC<{
  value: string;
  placeholder: string;
  testId: string;
  ariaLabel: string;
  className?: string;
  onChange: (value: string) => void;
}> = ({ value, placeholder, testId, ariaLabel, className, onChange }) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const autoSize = useCallback((el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  useLayoutEffect(() => {
    if (ref.current) autoSize(ref.current);
  }, [value, autoSize]);
  return (
    <textarea
      ref={ref}
      rows={1}
      data-testid={testId}
      aria-label={ariaLabel}
      value={value}
      placeholder={placeholder}
      onChange={(e) => {
        onChange(e.target.value);
        autoSize(e.currentTarget);
      }}
      className={`w-full resize-none overflow-hidden bg-transparent placeholder:text-muted-foreground focus:outline-none ${className ?? 'text-sm text-foreground'}`}
    />
  );
};

/**
 * Visual-first editor for a single information block. See module docs for the
 * upload, caption, and anti-overwrite-race contract.
 */
export const InformationBlockEditor: React.FC<InformationBlockEditorProps> = ({
  field,
  workspaceId,
  onUpdateField,
  surroundingFields,
  campaignGoal,
  committedImageKeys,
}) => {
  const { supabase } = useSupabase();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [uploading, setUploading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [imageBroken, setImageBroken] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [captionStatus, setCaptionStatus] = useState<CaptionStatus>('idle');
  // Whether another caption attempt can succeed; the Retry control follows it.
  const [captionRetryable, setCaptionRetryable] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [klipyOpen, setKlipyOpen] = useState(false);
  // Instant, environment-independent preview of the just-picked file. Preferred
  // over the served imageUrl so the thumbnail appears immediately and does not
  // depend on the public serve route resolving in the authoring environment.
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const localPreviewUrlRef = useRef<string | null>(null);
  localPreviewUrlRef.current = localPreviewUrl;

  // Whether the author has typed into each slot. Once true, the AI caption
  // pipeline must never overwrite that slot (the anti-overwrite race rule).
  const titleTouchedRef = useRef(false);
  const directiveTouchedRef = useRef(false);

  // Live values, mirrored into refs so the async caption response can compare
  // against the value at apply time without a stale closure.
  const titleRef = useRef(field.title ?? '');
  const directiveRef = useRef(field.directive ?? '');
  titleRef.current = field.title ?? '';
  directiveRef.current = field.directive ?? '';

  // The storage key of the active image, tracked locally so retry works even
  // before the parent has re-rendered this field with the new key.
  const storageKeyRef = useRef<string | undefined>(field.imageStorageKey);
  if (field.imageStorageKey) storageKeyRef.current = field.imageStorageKey;

  // Revoke the local preview object URL when the editor unmounts.
  useEffect(
    () => () => {
      safeRevokeObjectUrl(localPreviewUrlRef.current);
    },
    [],
  );

  /** Read a fresh Supabase access token; throws when unauthenticated. */
  const getToken = useCallback(async (): Promise<string> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) {
      throw new Error('Not authenticated');
    }
    return token;
  }, [supabase]);

  /**
   * Request an AI caption for the just-uploaded image. Fires immediately after
   * upload (not awaited by the caller) so the author can keep typing.
   *
   * @param storageKey R2 key of the uploaded image.
   */
  const requestCaption = useCallback(
    async (storageKey: string) => {
      // Snapshot the slots at request time. We only apply the model's title or
      // directive if the live value still equals this snapshot AND the author
      // never typed into it.
      const titleSnapshot = titleRef.current;
      const directiveSnapshot = directiveRef.current;
      const hasUserTitle = titleTouchedRef.current;
      const hasUserDirective = directiveTouchedRef.current;

      setCaptionStatus('pending');
      let retryable = true;
      try {
        const token = await getToken();
        const res = await fetch(`${offlineSessionWorkerUrl()}/information-block/caption`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            workspaceId,
            imageStorageKey: storageKey,
            hasUserTitle,
            hasUserDirective,
            campaignGoal,
            surroundingFields,
          }),
        });
        if (!res.ok) {
          // Only the worker's retry decision is read from the body, never its
          // text; without one, any 4xx but 408 and 429 rejects the request.
          const problem = (await res.json().catch(() => null)) as { retryable?: unknown } | null;
          retryable = typeof problem?.retryable === 'boolean' ? problem.retryable : isRetryableHttpStatus(res.status);
          throw new Error(`Caption failed (${res.status})`);
        }
        const data = (await res.json()) as CaptionResponse;

        const patch: InformationBlockPatch = {};
        // Caption always applies: it is holito's only surrogate for the image.
        if (typeof data.caption === 'string' && data.caption.length > 0) {
          patch.caption = data.caption;
        }
        // Title only applies when untouched AND unchanged since the request.
        if (
          !titleTouchedRef.current &&
          titleRef.current === titleSnapshot &&
          typeof data.title === 'string' &&
          data.title.length > 0
        ) {
          patch.title = data.title;
        }
        if (
          !directiveTouchedRef.current &&
          directiveRef.current === directiveSnapshot &&
          typeof data.directive === 'string' &&
          data.directive.length > 0
        ) {
          patch.directive = data.directive;
        }
        if (Object.keys(patch).length > 0) {
          onUpdateField(patch);
        }
        setCaptionStatus('idle');
      } catch (err) {
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'InformationBlockEditor.requestCaption',
          { workspaceId, retryable },
        );
        setCaptionRetryable(retryable);
        setCaptionStatus('failed');
      }
    },
    [campaignGoal, getToken, onUpdateField, surroundingFields, workspaceId],
  );

  /** Best-effort delete of an R2 object; failures are swallowed and captured. */
  const deleteObject = useCallback(
    async (storageKey: string) => {
      try {
        const token = await getToken();
        await fetch(`${offlineSessionWorkerUrl()}/information-block/delete`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ workspaceId, imageStorageKey: storageKey }),
        });
      } catch (err) {
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'InformationBlockEditor.deleteObject',
          { workspaceId },
        );
      }
    },
    [getToken, workspaceId],
  );

  /** Validate then upload a picked file; on success fire the caption request. */
  const handleFile = useCallback(
    async (file: File) => {
      setValidationError(null);
      if (!isAllowedInformationBlockMime(file.type)) {
        setValidationError(INFORMATION_BLOCK_MIME_REJECT_MESSAGE);
        return;
      }
      if (file.size > INFORMATION_BLOCK_MAX_BYTES) {
        setValidationError(informationBlockSizeRejectMessage());
        return;
      }

      // Instant local preview: show the author's image immediately, regardless
      // of whether the served URL resolves in this environment.
      const preview = safeCreateObjectUrl(file);
      if (preview) {
        safeRevokeObjectUrl(localPreviewUrlRef.current);
        setLocalPreviewUrl(preview);
      }

      setUploading(true);
      setImageBroken(false);
      try {
        const token = await getToken();
        const form = new FormData();
        form.append('file', file);
        if (workspaceId) form.append('workspaceId', workspaceId);
        const res = await fetch(`${offlineSessionWorkerUrl()}/information-block/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
        if (!res.ok) {
          throw new Error(`Upload failed (${res.status})`);
        }
        const data = (await res.json()) as UploadResponse;
        storageKeyRef.current = data.imageStorageKey;
        onUpdateField({
          imageStorageKey: data.imageStorageKey,
          imageSource: 'upload',
          imageUrl: data.imageUrl,
          imageStillUrl: undefined,
          mimeType: data.mimeType,
          gifId: undefined,
          gifSlug: undefined,
        });
        // Fire-and-forget: the author keeps typing while the caption lands.
        void requestCaption(data.imageStorageKey);
      } catch (err) {
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'InformationBlockEditor.handleFile',
          { workspaceId },
        );
        setValidationError('Upload failed. Try again');
      } finally {
        setUploading(false);
      }
    },
    [getToken, onUpdateField, requestCaption, workspaceId],
  );

  const onPickFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (file) void handleFile(file);
    },
    [handleFile],
  );

  /** Accept a file dropped onto the image slot. */
  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const file = e.dataTransfer.files?.[0];
      if (file) void handleFile(file);
    },
    [handleFile],
  );

  /** Select a direct KLIPY rendition and release any replaced draft upload. */
  const handleKlipySelect = useCallback((gif: KlipyGif) => {
    const previousKey = field.imageStorageKey ?? storageKeyRef.current;
    storageKeyRef.current = undefined;
    safeRevokeObjectUrl(localPreviewUrlRef.current);
    setLocalPreviewUrl(null);
    setImageBroken(false);
    setLightboxOpen(false);
    setCaptionStatus('idle');
    onUpdateField({
      imageStorageKey: undefined,
      imageUrl: gif.imageUrl,
      imageStillUrl: gif.imageStillUrl,
      mimeType: gif.mimeType,
      imageSource: 'klipy',
      gifId: gif.id,
      gifSlug: gif.slug,
      caption: gif.title,
    });
    if (previousKey && !committedImageKeys?.has(previousKey)) {
      void deleteObject(previousKey);
    }
  }, [committedImageKeys, deleteObject, field.imageStorageKey, onUpdateField]);

  /** Clear the image fields (keep title/directive) and delete the old object. */
  const handleRemoveImage = useCallback(() => {
    const previousKey = field.imageStorageKey ?? storageKeyRef.current;
    storageKeyRef.current = undefined;
    safeRevokeObjectUrl(localPreviewUrlRef.current);
    setLocalPreviewUrl(null);
    setImageBroken(false);
    setLightboxOpen(false);
    setCaptionStatus('idle');
    const textFallback = field.responseMode === 'passive' && field.body?.trim() ? { contentVariant: 'text' as const } : {};
    onUpdateField(field.imageSource === 'klipy' ? {
      ...textFallback,
      imageStorageKey: undefined,
      imageSource: undefined,
      imageUrl: undefined,
      imageStillUrl: undefined,
      mimeType: undefined,
      gifId: undefined,
      gifSlug: undefined,
      caption: undefined,
    } : { ...textFallback, imageStorageKey: undefined, imageUrl: undefined, mimeType: undefined });
    // Never delete media the SAVED campaign still references (edit mode):
    // cancelling the edit must leave the saved config's images intact.
    if (previousKey && !committedImageKeys?.has(previousKey)) {
      void deleteObject(previousKey);
    }
  }, [committedImageKeys, deleteObject, field.body, field.responseMode, field.imageSource, field.imageStorageKey, onUpdateField]);

  // Lightbox closes on Escape.
  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightboxOpen]);

  // Prefer the instant local preview; fall back to the served URL (e.g. when
  // re-opening a saved block). `imageBroken` only trips on the served URL.
  const displayUrl = localPreviewUrl ?? field.imageUrl;
  const hasImage = Boolean(displayUrl) && !imageBroken;

  return (
    <div data-testid="info-block-editor" className="flex gap-4">
      {/* LEFT: fixed 192x128 image slot (click or drag-and-drop). */}
      <div
        className="shrink-0"
        onDragOver={(e) => {
          e.preventDefault();
          if (!dragActive) setDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragActive(false);
        }}
        onDrop={onDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          data-testid="info-block-file-input"
          accept={FILE_INPUT_ACCEPT}
          onChange={onPickFile}
          className="hidden"
        />

        {uploading ? (
          <div data-testid="info-block-skeleton" aria-busy="true" className="h-32 w-48">
            <Skeleton height={128} width={192} borderRadius={8} />
          </div>
        ) : hasImage ? (
          <div className="group/slot relative h-32 w-48 overflow-hidden rounded-lg border border-border">
            <button
              type="button"
              data-testid="info-block-open-lightbox"
              aria-label="View image"
              onClick={() => setLightboxOpen(true)}
              className="block h-full w-full cursor-zoom-in"
            >
              <img
                data-testid="info-block-thumbnail"
                src={displayUrl}
                alt={field.caption || field.title || 'information block image'}
                onError={() => {
                  // Only the served URL can fail to load; an object URL always
                  // resolves. If the local preview is active, ignore the error.
                  if (!localPreviewUrl) setImageBroken(true);
                }}
                className="h-full w-full object-cover"
              />
              <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 transition-opacity group-hover/slot:opacity-100 group-focus-within/slot:opacity-100">
                <EyeGlyph />
              </span>
            </button>
            <button
              type="button"
              data-testid="info-block-remove"
              aria-label="Remove image"
              onClick={handleRemoveImage}
              className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-lg bg-black/50 text-white opacity-0 transition-opacity hover:bg-black/70 group-hover/slot:opacity-100 group-focus-within/slot:opacity-100 focus:opacity-100"
            >
              <svg aria-hidden="true" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M6 18L18 6" />
              </svg>
            </button>
            {field.imageSource === 'klipy' && (
              <KlipyAttribution className="absolute bottom-1 left-1 max-w-[calc(100%-8px)]" />
            )}
          </div>
        ) : imageBroken ? (
          <div className="flex h-32 w-48 flex-col items-center justify-center gap-2 rounded-lg border border-border bg-background text-center text-xs text-muted-foreground">
            <p data-testid="info-block-image-fallback">couldn't load preview</p>
            <button
              type="button"
              aria-label="Replace image"
              onClick={() => fileInputRef.current?.click()}
              className="rounded-lg border border-border px-2 py-1 text-xs text-foreground hover:bg-surface-hover"
            >
              Replace
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid="info-block-dropzone"
            aria-label="Add image"
            onClick={() => fileInputRef.current?.click()}
            className={`flex h-32 w-48 items-center justify-center rounded-lg border-2 border-dashed bg-background transition-colors ${
              dragActive
                ? 'border-volt text-foreground'
                : 'border-border text-muted-foreground hover:border-volt/60 hover:text-foreground'
            }`}
          >
            <PlusGlyph />
          </button>
        )}

        <button
          type="button"
          aria-label="Find GIF"
          onClick={() => setKlipyOpen(true)}
          className="mt-1.5 rounded-lg border border-border px-2.5 py-1 text-xs font-semibold text-foreground transition hover:border-volt/60 hover:bg-surface-hover"
        >
          GIF
        </button>

        {validationError && (
          <p data-testid="info-block-error" className="mt-1.5 w-48 text-xs text-danger">
            {validationError}
          </p>
        )}
      </div>

      {/* RIGHT: title + directive. While the AI writes them, themed skeletons
          stand in (the same loading primitive the rest of the form uses). */}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {captionStatus === 'pending' ? (
          <div data-testid="info-block-caption-pending" className="flex flex-col gap-2 py-1">
            <Skeleton height={18} width="55%" />
            <Skeleton height={18} width="85%" />
          </div>
        ) : (
          <>
            <BareTextarea
              value={field.title ?? ''}
              placeholder="Title (optional)"
              testId="info-block-title"
              ariaLabel="Information block title"
              className="text-base font-semibold text-foreground"
              onChange={(value) => {
                titleTouchedRef.current = true;
                onUpdateField({ title: value });
              }}
            />
            {field.responseMode === 'passive' ? <BareTextarea
              value={field.body ?? ''}
              placeholder="Information text (optional)"
              testId="info-block-body"
              ariaLabel="Information text"
              onChange={(body) => onUpdateField({ body })}
            /> : <BareTextarea
              value={field.directive ?? ''}
              placeholder="What should the respondent do with this?"
              testId="info-block-directive"
              ariaLabel="Information block directive"
              onChange={(value) => {
                directiveTouchedRef.current = true;
                onUpdateField({ directive: value });
              }}
            />}
          </>
        )}

        {captionStatus === 'failed' && (
          <div className="flex items-center gap-2 text-xs text-danger">
            <span>couldn't generate suggestions</span>
            {captionRetryable && <button
              type="button"
              data-testid="info-block-caption-retry"
              onClick={() => {
                const key = storageKeyRef.current;
                if (key) void requestCaption(key);
              }}
              className="rounded-lg border border-border px-2 py-0.5 text-foreground hover:bg-surface-hover"
            >
              Retry
            </button>}
          </div>
        )}
      </div>

      {/* Lightbox: full image, Escape + backdrop close. */}
      {lightboxOpen && hasImage && (
        <div
          data-testid="info-block-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => setLightboxOpen(false)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-8"
        >
          <img
            src={displayUrl}
            alt={field.caption || field.title || 'information block image'}
            onError={() => {
              if (!localPreviewUrl) setImageBroken(true);
              setLightboxOpen(false);
            }}
            className="max-h-full max-w-full rounded-lg"
          />
        </div>
      )}

      <KlipyPicker
        open={klipyOpen}
        onClose={() => setKlipyOpen(false)}
        onSelect={handleKlipySelect}
      />
    </div>
  );
};

export default InformationBlockEditor;
